<template>
  <div class="speedometer">
    <svg :width="widthCss" :height="heightCss" :viewBox="viewBox">
      <!-- Gradient Definition for Pie Slice -->
      <defs>
        <linearGradient id="pieGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" :style="{ stopColor: colors.tertiary, stopOpacity: 0.05 }" />
          <stop offset="100%" :style="{ stopColor: colors.tertiary, stopOpacity: 0.15 }" />
        </linearGradient>
      </defs>

      <!-- Remaining Speed Arc -->
      <path
        :d="remainingArcPath"
        :stroke="colors.secondary"
        :stroke-width="strokeWidthCss"
        fill="none"
        stroke-linecap="round"
      />

      <!-- Speedometer Arc -->
      <path
        :d="arcPath"
        :stroke="colors.primary"
        :stroke-width="strokeWidthCss"
        fill="none"
        stroke-linecap="round"
      />

      <!-- Pie Slice for Gradient Overlay -->
      <path :d="piePath" fill="url(#pieGradient)" />

      <!-- Needle (Custom Shape) -->
      <path :d="needlePath" :fill="colors.tertiary" />
    </svg>
    <ChartLegend class="chart" :style="{ width: '13.75rem' , height: '2.1875rem' }" :data="data" fontSize='large' />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import ChartLegend from '@/components/common/ChartLegend.vue'
import type { PropType } from 'vue'
import type { IChartDataItem } from '@/components/common/common'

const props = defineProps({
  data: {
    type: Array as PropType<IChartDataItem[]>,
    required: true,
    validator: (value: IChartDataItem[]) => {
      if (value.length > 2) {
        console.error('The "data" prop can contain at most 2 items.')
        return false
      }
      return true
    }
  },
  width: {
    type: [Number, String],
    default: '14rem'
  },
  height: {
    type: [Number, String],
    default: '6.875rem'
  },
  strokeWidth: {
    type: [Number, String],
    default: '0.5rem'
  },
  colors: {
    type: Object,
    default: () => ({
      primary: '#939393', // Color for the main arc
      secondary: '#565656', // Color for the remaining arc
      tertiary: '#414141' // Color for the needle
    })
  }
})

const toPx = (value: string | number, useStaticRemBase = false): number => {
  if (typeof value === 'number') return value
  const trimmed = value.trim()
  if (trimmed.endsWith('rem')) {
    const rem = parseFloat(trimmed)
    const base = useStaticRemBase
      ? 16
      : parseFloat(getComputedStyle(document.documentElement).fontSize || '16')
    return rem * base
  }
  if (trimmed.endsWith('px')) {
    return parseFloat(trimmed)
  }
  const numeric = parseFloat(trimmed)
  return Number.isNaN(numeric) ? 0 : numeric
}

const widthPx = computed(() => toPx(props.width))
const heightPx = computed(() => toPx(props.height))
// Use a static rem base so stroke thickness stays consistent across breakpoints
const strokeWidthPx = computed(() => toPx(props.strokeWidth, true))
const widthCss = computed(() => (typeof props.width === 'number' ? `${props.width}px` : props.width))
const heightCss = computed(() => (typeof props.height === 'number' ? `${props.height}px` : props.height))
const strokeWidthCss = computed(() => `${strokeWidthPx.value}px`)
const viewBox = computed(() => {
  const padding = 0
  const yOffset = heightPx.value * 0.25 + strokeWidthPx.value * 2
  return `${-padding} ${-yOffset} ${widthPx.value + padding * 2} ${heightPx.value + padding * 2}`
})

// Watch the `colors` prop to react to color changes
watch(
  () => props.colors,
  (newColors) => {
    console.log('Colors changed:', newColors)
  },
  { deep: true }
)

// Max speed (clamped to 100)
const maxSpeed = 100

// Compute the total value from the data
const totalValue = computed(() => props.data.reduce((sum, item) => sum + (item.value || 0), 0))

// Compute the percentage speed based on the first data item's value
const speed = computed(() => {
  if (!props.data.length || totalValue.value === 0) return 0
  const firstValue = props.data[0]!.value || 0
  if (firstValue === 0) return 0
  return (firstValue / totalValue.value) * 100
})

// max arc radius

const arcRadius = computed(() => heightPx.value * 0.75)

// Needle length (radius of the pie slice)
const needleLength = computed(() => heightPx.value * 0.65)

// Arc path for the speedometer
const arcPath = computed(() => {
  const clampedSpeed = Math.min(speed.value, maxSpeed)
  let startAngle = -180 // Starting at the leftmost point
  let endAngle = (clampedSpeed / maxSpeed) * 180 - 180

  if (clampedSpeed >= 95) {
    endAngle = endAngle - 7.5
  }

  const start = polarToCartesian(widthPx.value / 2, heightPx.value / 2, arcRadius.value, startAngle)
  const end = polarToCartesian(widthPx.value / 2, heightPx.value / 2, arcRadius.value, endAngle)

  const largeArcFlag = (clampedSpeed / maxSpeed) * 180 > 180 ? 1 : 0

  return `M ${start.x} ${start.y} A ${arcRadius.value} ${arcRadius.value} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`
})

// Remaining arc (Unfilled portion)
const remainingArcPath = computed(() => {
  const clampedSpeed = Math.min(speed.value, maxSpeed)

  let remainingStartAngle = -180
  let remainingEndAngle = 360

  if (clampedSpeed >= 0) {
    remainingStartAngle = (clampedSpeed / maxSpeed) * 180 + 180 + 7.5
  }

  if (clampedSpeed >= 95) {
    remainingStartAngle = (clampedSpeed / maxSpeed) * 180 + 180
    remainingEndAngle = 360
  }

  const start = polarToCartesian(
    widthPx.value / 2,
    heightPx.value / 2,
    arcRadius.value,
    remainingStartAngle
  )
  const end = polarToCartesian(
    widthPx.value / 2,
    heightPx.value / 2,
    arcRadius.value,
    remainingEndAngle
  )

  const remainingAngle = remainingEndAngle - remainingStartAngle
  const largeArcFlag = remainingAngle > 180 ? 1 : 0

  return `M ${start.x} ${start.y} A ${arcRadius.value} ${arcRadius.value} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`
})

// Pie Slice Path Calculation (Gradient Overlay)
const piePath = computed(() => {
  const clampedSpeed = Math.min(speed.value, maxSpeed)
  let startAngle = -180
  let endAngle = (clampedSpeed / maxSpeed) * 180 - 180

  if (clampedSpeed >= 95) {
    endAngle = endAngle - 7.5
  }

  const start = polarToCartesian(
    widthPx.value / 2,
    heightPx.value / 2,
    needleLength.value,
    startAngle
  )
  const end = polarToCartesian(widthPx.value / 2, heightPx.value / 2, needleLength.value, endAngle)

  const largeArcFlag = (clampedSpeed / maxSpeed) * 180 > 180 ? 1 : 0

  return `M ${widthPx.value / 2} ${heightPx.value / 2} L ${start.x} ${start.y} A ${needleLength.value} ${needleLength.value} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`
})

// Needle Shape Path Calculation (Rounded Bottom, Pointed Top)
const needlePath = computed(() => {
  let angle = (speed.value / maxSpeed) * 180 - 180

  if (speed.value >= 95) {
    angle = angle - 7.5
  }

  angle = Math.max(Math.min(angle, 0), -180)

  const radians = (angle * Math.PI) / 180
  const needleWidth = 2
  const needleBaseRadius = 3

  const bottomX = widthPx.value / 2 + needleBaseRadius * Math.cos(radians + Math.PI)
  const bottomY = heightPx.value / 2 + needleBaseRadius * Math.sin(radians + Math.PI)

  const topX = widthPx.value / 2 + needleLength.value * Math.cos(radians)
  const topY = heightPx.value / 2 + needleLength.value * Math.sin(radians)

  const leftX =
    widthPx.value / 2 +
    (needleBaseRadius + needleWidth / 2) * Math.cos(radians + Math.PI / 2)
  const leftY =
    heightPx.value / 2 +
    (needleBaseRadius + needleWidth / 2) * Math.sin(radians + Math.PI / 2)

  const rightX =
    widthPx.value / 2 +
    (needleBaseRadius + needleWidth / 2) * Math.cos(radians - Math.PI / 2)
  const rightY =
    heightPx.value / 2 +
    (needleBaseRadius + needleWidth / 2) * Math.sin(radians - Math.PI / 2)

  return `M ${leftX} ${leftY} L ${bottomX} ${bottomY} L ${rightX} ${rightY} L ${topX} ${topY} Z`
})

// Helper to convert polar coordinates to Cartesian
function polarToCartesian(cx: number, cy: number, radius: number, angle: number) {
  const radians = (angle * Math.PI) / 180
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians)
  }
}
</script>

<style scoped>
.speedometer {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
